import { useState } from 'react'

import { Box, Button, HeaderText, Text } from '@factorialco/deprecated-design-system'
import { DocumentsDataTableDocument } from 'generated/resources/datatable.graphql'
import { UpdateStatusFilledDocument } from 'generated/resources/graphql'
import { DocumentForPreviewDocument } from 'generated/resources/graphql'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { canFill, isPendingToFill } from 'modules/documents/lib/Documents'
import { DocumentStatuses } from 'modules/documents/lib/enums'
import { LogTypes } from 'modules/documents/lib/enums'
import Logs from 'modules/documents/store/collections/logs'
import type { EsignatureRequest } from 'modules/esignatures/hooks/requests/useGetEsignatureRequests'

import FillableModal from './Modal'
import SignatureRequests from './SignatureRequests'
import FillableStatus from './Status'

interface Props {
  document: Document
  signatureRequests: EsignatureRequest[]
  saving: boolean
  disableFilleableConfirm: boolean
}

const FillablePDF = ({ document, signatureRequests, saving, disableFilleableConfirm }: Props) => {
  const { employee: currentEmployee } = useCurrentSession()
  const [openModal, setOpenModal] = useState(false)
  const [disabledButton, setDisabledButton] = useState(false)
  const [logs] = useState(new Logs())
  const descriptionText = (status: string) => description[status]
  const [updateStatusFilled] = useMutation(UpdateStatusFilledDocument, {
    extractorFn: (data) => data.documentsMutations.updateStatusFilledDocumentsDocument,
  })
  const description: Record<string, string> = {
    [DocumentStatuses.ToFill]: (currentEmployee ? canFill(document, currentEmployee.id) : false)
      ? i18n.t('documents.modal.fillable.description.to_fill')
      : i18n.t('documents.modal.fillable.description.to_fill_others'),
    [DocumentStatuses.Filled]: i18n.t('documents.modal.fillable.description.filled'),
  }

  const { loading } = useAsyncData(
    async () =>
      Promise.all([
        logs.fetch({ data: { document_id: document.id, log_type: LogTypes.StatusChanged } }),
      ]),
    [document.id]
  )

  const additionalSignees = signatureRequests.filter((request) => {
    const employee = document.employee
    return request.signee?.id !== (employee && employee.accessId)
  })

  const {
    access: { id: accessId },
  } = useCurrentSession()
  const accessSignatureRequest = signatureRequests.filter(
    (request) => request.signee?.id == accessId
  )

  const onSubmit = async () => {
    setDisabledButton(true)
    const { success } = await asyncAction(() =>
      updateStatusFilled({
        variables: {
          id: document.id,
        },
        refetchQueries: [DocumentsDataTableDocument, DocumentForPreviewDocument],
      })
    )

    if (success) {
      // This event allow the mobile webview to know that the document was filled
      window.postMessage({ event: 'factorialFilled', documentId: document.id })

      setOpenModal(false)
      setDisabledButton(false)
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }
  }

  return (
    <Box gap="s24">
      {openModal && (
        <FillableModal
          saving={saving}
          disabledButton={disabledButton}
          onSubmit={onSubmit}
          onClose={() => setOpenModal(false)}
          signatureRequests={accessSignatureRequest}
        />
      )}
      {document.status && (
        <Box gap="s8">
          <HeaderText text={i18n.t('documents.modal.fillable.title')} />
          <Text size="300" color="grey800">
            {descriptionText(document.status)}
          </Text>
        </Box>
      )}
      <FillableStatus document={document} statusChangedLog={logs.models.at(-1)} />
      <Box gap="s16">
        {isPendingToFill(document) && (
          <SignatureRequests
            document={document}
            loading={loading}
            additionalSignees={additionalSignees}
          />
        )}
        {currentEmployee && canFill(document, currentEmployee.id) && (
          <Box alignItems="flexEnd">
            <Button disabled={disableFilleableConfirm} onClick={() => setOpenModal(true)}>
              {i18n.t('documents.modal.fillable.modal.confirm')}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default FillablePDF
